// Work type (Assessment / Homework). It belongs to the assignment and is fixed
// on an attempt series when it starts (see server/workClassification.js).
export const WORK_TYPE_FILTERS = Object.freeze([
  ["all", "All work"],
  ["assessment", "Assessments"],
  ["homework", "Homework"],
]);

// An assignment without a work type is a legacy Assessment. A value that is
// neither type is shown as unclear (null) rather than guessed.
export const assignmentWorkType = (assignment) => {
  if (!assignment || !Object.prototype.hasOwnProperty.call(assignment, "workType")) return "assessment";
  return assignment.workType === "homework" || assignment.workType === "assessment" ? assignment.workType : null;
};

export const workTypeLabel = (type) =>
  type === "homework" ? "Homework" : type === "assessment" ? "Assessment" : "Type unclear";

// The noun used in action labels: "Start homework", "Continue assessment".
export const workNoun = (type) => type === "homework" ? "homework" : type === "assessment" ? "assessment" : "work";

export const matchesWorkType = (type, filter) => filter === "all" || type === filter;

const millis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

// Browser copy of the server's series rule, for screens that read attempts
// directly (dashboards): Attempt 1's recorded type; no record anywhere means a
// legacy Assessment, unless a Homework assignment existed before the series
// started (only an older backend could have started it), which is unclear.
export const seriesWorkType = (sessions = [], assignment = null) => {
  const anchor = [...sessions].sort((left, right) =>
    (Number(left.attemptNumber) || 1) - (Number(right.attemptNumber) || 1))[0];
  if (anchor && Object.prototype.hasOwnProperty.call(anchor, "workTypeSnapshot")) {
    return anchor.workTypeSnapshot === "homework" || anchor.workTypeSnapshot === "assessment"
      ? anchor.workTypeSnapshot : null;
  }
  if (assignment && assignment.revokedAt == null && assignment.workType === "homework") {
    const startedAt = millis(anchor?.startedAt);
    const assignedAt = millis(assignment.assignedAt);
    if (startedAt === null || assignedAt === null || startedAt >= assignedAt) return null;
  }
  return "assessment";
};

// Averages each category with the caller's existing averaging function, so a
// page keeps its own rules. Items of unclear type count only in "all".
export const averagesByWorkType = (items, average) => ({
  all: average(items.map(({ percentage }) => percentage)),
  assessment: average(items.filter(({ workType }) => workType === "assessment").map(({ percentage }) => percentage)),
  homework: average(items.filter(({ workType }) => workType === "homework").map(({ percentage }) => percentage)),
  assessmentCount: items.filter(({ workType }) => workType === "assessment").length,
  homeworkCount: items.filter(({ workType }) => workType === "homework").length,
});

// Attaches each student's series type from a results response
// (`seriesWorkTypes`). A series the server did not classify is a legacy
// Assessment; an unclear one is null.
export const withSeriesWorkTypes = (series, seriesWorkTypes) => {
  const types = new Map((Array.isArray(seriesWorkTypes) ? seriesWorkTypes : []).map(({ studentUid, workType }) =>
    [studentUid, workType === "homework" || workType === "assessment" ? workType : null]));
  return series.map((item) => ({
    ...item,
    workType: types.has(item.studentUid) ? types.get(item.studentUid) : "assessment",
  }));
};
